import React, { useState, useCallback } from 'react';
import './LabelBuilder.less';
import { Switch, Route, Redirect } from 'react-router-dom';
import GeneratorProductTable from 'components/GeneratorProductTable/GeneratorProductTable';
import { patchProductLabelBarcode } from 'util/label-barcode-resolve';
import * as api from 'util/api-morpheus.js';
import LabelGeneratorFormContent from './LabelGeneratorFormContent/LabelGeneratorFormContent';
import NewLabelBuilder from './NewLabelBuilder';

/** Pallet quantity is printed on the label; blank → write-in. Other types default to 1 copy. */
function resolvePrintQuantity(type, quantity) {
  if (type === 'pallet') {
    if (quantity == null || quantity === '') return null;
    return quantity;
  }
  return quantity || 1;
}

const INITIAL_FORM_VALUES = {
  keg: {
    dateType: 'kegged',
  },
  case: {
    dateType: 'bestBefore',
  },
  slab: {
    dateType: 'bestBefore',
  },
  pallet: {
    dateType: 'bestBefore',
  },
};

const LabelBuilder = () => {
  const [generatorStatus, setGeneratorStatus] = useState({
    active: false,
    error: null,
    success: false,
  });

  // Set once a PDF has been successfully generated
  const [labelPDF, setLabelPDF] = useState(null);

  const onGeneratorReset = useCallback(() => {
    setGeneratorStatus({
      active: false,
      error: null,
      success: false,
    });
  }, []);

  const handlePrint = async ({ type, data }) => {
    const { formData, product } = data;
    const productForLabels = patchProductLabelBarcode(product, {
      labelType: type,
      unitSize: formData.unitSize,
      palletType: formData.palletType,
    });
    setGeneratorStatus({
      active: true,
      error: null,
      success: false,
    });
    try {
      const pdf = await api.generatePackingLabels({
        type,
        productId: product.id,
        product: productForLabels,
        formData: {
          ...formData,
          quantity: resolvePrintQuantity(type, formData.quantity),
        },
      });

      setLabelPDF(pdf);

      setGeneratorStatus({
        active: false,
        error: null,
        success: true,
      });
    } catch (error) {
      const apiErr = error.response?.data?.error;
      setGeneratorStatus({
        active: false,
        error: apiErr?.userError
          ?? apiErr?.message
          ?? 'There was an error generating the labels',
        success: false,
      });
    }
  };

  return (
    <>
      <Switch>
        <Route
          exact
          path="/dashboard/label-builder/new"
          render={() => (
            <NewLabelBuilder
              onPrint={handlePrint}
              generatorStatus={generatorStatus}
              labelPDF={labelPDF}
              onGeneratorReset={onGeneratorReset}
            />
          )}
        />
        {[
          { type: 'keg' },
          { type: 'case' },
          { type: 'slab' },
          { type: 'pallet' },
        ].map(({ type }) => (
          <Route
            key={type}
            exact
            path={`/dashboard/label-builder/${type}`}
            render={() => (
              <GeneratorProductTable
                type={type}
                selectionType="single"
                pluralLabel={`${type} labels`}
                GeneratorFormContent={LabelGeneratorFormContent}
                initialFormValues={(products) => {
                  const initialValues = {
                    certifiedFresh: false,
                  };
                  products.forEach(({ id }) => {
                    if (Object.keys(INITIAL_FORM_VALUES).includes(type)) {
                      Object.entries(INITIAL_FORM_VALUES[type]).forEach(([field, value]) => {
                        initialValues[`${id}.${field}`] = value;
                      });
                    }
                  });
                  return initialValues;
                }}
                onGenerate={(data) => handlePrint({ type, data })}
                generatorStatus={generatorStatus}
                generatorOutput={{
                  type: 'pdf',
                  data: labelPDF,
                }}
                onGeneratorReset={onGeneratorReset}
              />
            )}
          />
        ))}
        <Redirect to="/dashboard/label-builder/new" />
      </Switch>
    </>
  );
};

export default LabelBuilder;
